import { type ActionEvent, Controller } from "@hotwired/stimulus"

import ComboboxController from "@/controllers/combobox_controller"
import { debounceAsync } from "@/helpers/utils"

interface Location {
  locationType: string
  maxDistance: string
  name: string
  slug: string
}

export default class extends Controller {
  static outlets = ["combobox"]
  declare readonly comboboxOutlet: ComboboxController

  static targets = [
    "firstResult",
    "hiddenInputLocationType",
    "hiddenInputMaxDistance",
    "hiddenInputSlug",
    "input"
  ]
  declare readonly firstResultTarget: HTMLElement
  declare readonly hiddenInputLocationTypeTarget: HTMLInputElement
  declare readonly hiddenInputMaxDistanceTarget: HTMLInputElement
  declare readonly hiddenInputSlugTarget: HTMLInputElement
  declare readonly inputTarget: HTMLInputElement

  static values = { endpoint: String, listboxId: String, selectedLocation: Object }
  declare readonly endpointValue: string
  declare readonly listboxIdValue: string
  declare selectedLocationValue: Location

  initialize(): void {
    this.lookup = debounceAsync(this.lookup.bind(this), 250)
  }

  selectedLocationValueChanged(): void {
    this.inputTarget.value = this.selectedLocationValue.name
    this.hiddenInputLocationTypeTarget.value = this.selectedLocationValue.locationType
    this.hiddenInputMaxDistanceTarget.value = this.selectedLocationValue.maxDistance
    this.hiddenInputSlugTarget.value = this.selectedLocationValue.slug
  }

  hideResultsIfBlurred(e: FocusEvent): void {
    // Controller element contains both input and "clear" button, each of which can gain/lose
    // focus. Keep combobox open if focus is still within controller element itself.
    if (this.hasFocusedElement(e.relatedTarget as Element)) {
      this.comboboxOutlet.start()
    } else {
      this.selectFirstResult()
      this.comboboxOutlet.stop()
    }
  }

  async lookup({ target }: ActionEvent): Promise<void> {
    const inputValue = (target as HTMLInputElement).value
    const url = new URL(this.endpointValue, window.location.origin)
    const params = new URLSearchParams({
      q: inputValue,
      listbox_id: this.listboxIdValue
    })
    url.search = params.toString()
    const result = await window.fetch(url.toString())
    const html = await result.text()

    window.Turbo.session.renderStreamMessage(html)
  }

  onFocus(): void {
    this.inputTarget.select()
  }

  resetSelected({ target }: ActionEvent): void {
    this.selectedLocationValue = {
      locationType: "",
      maxDistance: "",
      name: (target as HTMLInputElement).value,
      slug: ""
    }
  }

  selectFirstResult(): void {
    // Auto-select result only if input has a value, so empty location can still be searched.
    if (this.inputTarget.value) {
      this.firstResultTarget.click()
    }
  }

  selectResult({ params: { result } }: ActionEvent): void {
    this.selectedLocationValue = result
  }

  private hasFocusedElement(element?: Element | null): boolean {
    return this.element.contains(element || document.activeElement)
  }
}
